import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AppFileUploader,
  AppRemixIcon,
  AppModal,
} from '@/layout/AppComponents';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { UnmountClosed } from 'react-collapse';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import { axiosPath } from '../../../../../../routing/helpers/router';
import AuxSidebar from '../../../../../Sidebar/AuxSidebar';
import Translator from '../../../../../components/helpers/Translator';
import { toFormData } from '../../../../../../minions/forms';
import errorHandler from '@/layout/components/error/ErrorHandler';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import colors from '../../../../../../constants/colors';

const ImportSideBarBlock = () => {
  const dispatch = useDispatch();
  const { moduleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const [openOnce, setOpenOnce] = useState(false);
  const [isDownloadingWorkXls, setIsDownloadingWorkXls] = useState(false);
  const [showImportingModal, setShowImportingModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [advancedOptions, setAdvancedOptions] = useState(false);
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const evidencesPermissions = useSelector(
    (s) =>
      s.module?.[moduleBridgeHex]?.permissions?.gcl?.module?.plant?.evidences
  );

  // > IMPORT XLS
  const handleImportXls = async (values) => {
    if (showImportingModal) return;
    setShowImportingModal(true);
    await axios
      .post(
        axiosPath("api_r_user_module_legalCompliance_import_importWorkXls"),
        toFormData({ ...values, plantHex }),
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then(() => window.location.reload(false))
      .catch((error) => {
        setShowImportingModal(false);
        setErrorMessage(
          error.response.data?.error || error.response.data || "Error"
        );
        dispatch(errorHandler(error));
      });
  };
  // < IMPORT XLS

  // > DOWNLOAD XLS
  const handleDownloadWorkXls = async () => {
    if (isDownloadingWorkXls) return;
    setIsDownloadingWorkXls(true);

    await axios
      .post(
        axiosPath("api_r_user_module_legalCompliance_import_downloadWorkXls"),
        { plantHex }
      )
      .then(({ data }) => {
        if (isMounted.current) {
          window.location = data.s3FileUrl;
        }
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => setIsDownloadingWorkXls(false));
  };
  // < DOWNLOAD XLS

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'importXls')
      dispatch(currentPageAction.change('auxSidebar', { opened: 'importXls' }));
    else dispatch(currentPageAction.change('auxSidebar', { opened: null }));
  };

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'importXls' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="upload-2" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="export" name="export.import.button" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="user" name="user.import.title" />}
        isOpen={auxSidebar && auxSidebar === 'importXls'}
        width="350px"
        position="right"
        onClose={() => handleAuxSidebar()}
      >
        {openOnce && (
          <>
            <Formik
              initialValues={{
                file: null,
                evidenceFile: null,
              }}
              validateOnChange={false}
              validateOnBlur={false}
              enableReinitialize
              onSubmit={handleImportXls}
            >
              {({ setFieldValue, handleSubmit, values, isSubmitting }) => (
                <>
                  <AppFileUploader
                    id={1}
                    placeholder={ReactDOMServer.renderToString(
                      values.file ? (
                        <Translator file="general" name="one.file.selected" />
                      ) : (
                        <Translator file="general" name="select.a.file" />
                      ),
                    )}
                    onChange={(opt) => setFieldValue('file', opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />

                  {evidencesPermissions?.zipImport && (
                    <>
                      <FilterBlockTitle
                        onClick={() => setAdvancedOptions(!advancedOptions)}
                      >
                        <AppRemixIcon
                          icon={advancedOptions ? 'arrow-down-s' : 'arrow-right-s'}
                          filled
                        />
                        <Translator
                          file="module-legalCompliance"
                          name="m-gcl-advanced.options"
                        />
                      </FilterBlockTitle>
                      <UnmountClosed isOpened={advancedOptions}>
                        <AdvancedBorder>
                          <InputFileLabel>
                            <Translator
                              file="module-legalCompliance"
                              name="m-glc-zip.file.containing.test.files"
                            />
                          </InputFileLabel>
                          <AppFileUploader
                            id={2}
                            placeholder={ReactDOMServer.renderToString(
                              values.evidenceFile ? (
                                <Translator file="general" name="one.file.selected" />
                              ) : (
                                <Translator
                                  file="module-legalCompliance"
                                  name="m-glc-zip.file.containing.test.files"
                                />
                              ),
                            )}
                            containerClassName="mb-0 mt-0"
                            onChange={(opt) => setFieldValue('evidenceFile', opt)}
                            btnText={<Translator file="general" name="search..." />}
                          />

                          <AppModal
                            trigger={
                              <span className="pointer">
                                <Translator
                                  file="module-legalCompliance"
                                  name="m-gcl-instructions.use"
                                />{' '}
                                <AppRemixIcon icon="question" size={16} />
                              </span>
                            }
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="m-gcl-instructions.use"
                              />,
                            )}
                            size="xl"
                            isConfirmBtn
                            confirmBtnText={ReactDOMServer.renderToString(
                              <Translator file="user" name="user.accept" />,
                            )}
                            onConfirm={() => {}}
                          >
                            <Translator
                              file="module-legalCompliance"
                              name="m-glc-zip.file.instructions.evidence"
                              isHtml
                            />
                          </AppModal>
                        </AdvancedBorder>
                      </UnmountClosed>
                    </>
                  )}
                  <span
                    className={`btn btn-xs btn-block btn-outline-light mt-2 mb-4${
                      isSubmitting || !values.file ? ' disabled' : ''
                    }`}
                    onClick={() =>
                      isSubmitting || !values.file ? undefined : handleSubmit()
                    }
                  >
                    <AppRemixIcon icon="file-excel" />{' '}
                    <Translator file="export" name="export.import.button" />
                  </span>
                </>
              )}
            </Formik>
            <span
              className={`btn btn-xs btn-block btn-success${
                isDownloadingWorkXls ? ' disabled' : ''
              }`}
              onClick={handleDownloadWorkXls}
            >
              <AppRemixIcon icon={isDownloadingWorkXls ? 'loader-4' : 'file-excel'} />{' '}
              <Translator file="export" name="export.xls.button" />
            </span>
          </>
        )}
      </AuxSidebar>
      <AppModal
        show={!!showImportingModal}
        title={<Translator file="module-legalCompliance" name="importing.dots" />}
      >
        <div className="progress">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: '100%' }}
          />
        </div>
      </AppModal>
      <AppModal
        show={!!errorMessage}
        type="danger"
        title={<Translator file="user" name="user.error" />}
        onClose={() => setErrorMessage(false)}
        onConfirm={() => setErrorMessage(false)}
        isConfirmBtn
        confirmBtnText={<Translator file="user" name="user.accept" />}
      >
        {errorMessage}
      </AppModal>
    </>
  );
};

export default ImportSideBarBlock;

const FilterBlockTitle = styled.div`
  color: ${colors.primary};
  font-size: 14px;
  margin-top: 10px;
  cursor: pointer;
`;

const InputFileLabel = styled.div`
  font-size: 12px;
  color: ${colors.primary};
  margin-top: 10px;
`;
const AdvancedBorder = styled.div`
  border: 1px solid ${colors.grey300};
  padding: 10px;
`;
